import { useMutation } from "@apollo/client";
import { useCallback } from "react";
import { Controller, useForm } from "react-hook-form";
import { useModal } from "@ebay/nice-modal-react";

import { createDrawer, createDrawerTrigger } from "ds/components/DrawerNew/utils";
import DrawerSimple from "ds/components/DrawerNew/Simple";
import Box from "ds/components/Box";
import Button from "ds/components/Button";
import FormField from "ds/components/Form/Field";
import Input from "ds/components/Input";
import Textarea from "ds/components/Textarea";
import TooltipModalBody from "ds/components/TooltipModal/Body";
import { TooltipModalTitle } from "ds/components/TooltipModal/Title";
import DrawerHeader from "ds/components/Drawer/Header";
import DrawerBody from "ds/components/Drawer/Body";
import DrawerFooter from "ds/components/Drawer/Footer";
import DrawerFooterActions from "ds/components/Drawer/FooterActions";
import FlashContext from "components/FlashMessages/FlashContext";
import useTypedContext from "hooks/useTypedContext";
import { AnalyticsPageOrganization } from "hooks/useAnalytics/pages/organization";
import { Role } from "types/generated";
import DrawerHeaderTitle from "ds/components/DrawerNew/HeaderTitle";
import DrawerCloseIcon from "ds/components/DrawerNew/CloseIcon";
import DrawerCancelButton from "ds/components/DrawerNew/CancelButton";

import { ROLE_UPDATE } from "../gql";
import { getTooltipAnalyticsProps, validateRequired } from "../FullScreenForm/utils";

type FormValues = {
  name: string;
  description: string;
};

type RoleEditDetailsDrawerProps = {
  role: Role;
  analyticsPage: AnalyticsPageOrganization;
  refetchQueries: string[];
};

const RoleEditDetailsDrawer = createDrawer<RoleEditDetailsDrawerProps>(
  ({ role, analyticsPage, refetchQueries }) => {
    const drawer = useModal();
    const { onError, reportSuccess } = useTypedContext(FlashContext);

    const [updateRole, { loading: updateLoading }] = useMutation(ROLE_UPDATE, {
      refetchQueries,
    });

    const form = useForm<FormValues>({
      defaultValues: {
        name: role.name,
        description: role.description,
      },
      mode: "onChange",
    });

    const {
      control,
      register,
      handleSubmit,
      formState: { errors },
    } = form;

    const onSubmit = useCallback(
      (formValues: FormValues) => {
        const input = {
          name: formValues.name,
          description: formValues.description,
          Actions: role.actions,
        };

        updateRole({
          variables: {
            id: role.id,
            input,
          },
        })
          .then(({ data }) => {
            if (data?.roleUpdate.id) {
              reportSuccess({ message: "Role details have been successfully updated" });
              drawer.hide();
            }
          })
          .catch(onError);
      },
      [updateRole, role.id, role.actions, drawer, reportSuccess, onError]
    );

    const handleFormSubmit = useCallback(() => {
      handleSubmit(onSubmit)();
    }, [onSubmit, handleSubmit]);

    return (
      <DrawerSimple variant="normal" dataTestId="role-edit-details-drawer">
        <DrawerHeader>
          <DrawerHeaderTitle title="Edit details" />
          <DrawerCloseIcon />
        </DrawerHeader>

        <DrawerBody>
          <Box direction="column" gap="large">
            <Controller
              name="name"
              control={control}
              rules={{ validate: validateRequired("Name") }}
              render={({ field, fieldState }) => (
                <FormField
                  label="Name"
                  error={errors?.name?.message}
                  tooltipInfoVariant="modal"
                  {...getTooltipAnalyticsProps("Edit details drawer", "Name", analyticsPage)}
                  tooltipInfo={
                    <>
                      <TooltipModalTitle>Name your role</TooltipModalTitle>
                      <TooltipModalBody align="start">
                        Name of this role. It should be unique within one account
                      </TooltipModalBody>
                    </>
                  }
                  noMargin
                >
                  {({ ariaInputProps }) => (
                    <Input
                      placeholder="Name your role"
                      ref={field.ref}
                      error={!!fieldState.error}
                      type="text"
                      value={field.value}
                      onChange={field.onChange}
                      name="name"
                      {...ariaInputProps}
                    />
                  )}
                </FormField>
              )}
            />
            <FormField
              label="Description"
              isOptional
              tooltipInfoVariant="modal"
              {...getTooltipAnalyticsProps("Edit details drawer", "Description", analyticsPage)}
              tooltipInfo={
                <>
                  <TooltipModalTitle>Describe your role</TooltipModalTitle>
                  <TooltipModalBody align="start">
                    Human-friendly description of this role - supports Markdown. This value is only
                    used to make life easier for you, the user. You can use this space to describe
                    your role, provide useful links, or embed a cat GIF. The only limit is yourself.
                  </TooltipModalBody>
                </>
              }
              noMargin
            >
              {({ ariaInputProps }) => (
                <Textarea
                  placeholder="Enter role description here..."
                  {...register("description")}
                  {...ariaInputProps}
                />
              )}
            </FormField>
          </Box>
        </DrawerBody>

        <DrawerFooter>
          <DrawerFooterActions>
            <DrawerCancelButton disabled={updateLoading} />

            <Button
              variant="primary"
              onPress={handleFormSubmit}
              loading={updateLoading}
              disabled={updateLoading}
            >
              Save
            </Button>
          </DrawerFooterActions>
        </DrawerFooter>
      </DrawerSimple>
    );
  }
);

export const showRoleEditDetailsDrawer = createDrawerTrigger(RoleEditDetailsDrawer);
